import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  jsonLd?: Record<string, any> | Array<Record<string, any>>;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly siteName = 'The Scenetale';
  private readonly siteBaseUrl = 'https://scenetale.com';
  private readonly defaultImage = 'https://scenetale.com/assets/images/icon.png';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Sets comprehensive SEO metadata including Title, Meta description,
   * Open Graph, Twitter Cards, Canonical URL, and JSON-LD structured data.
   */
  public setSeo(config: SeoConfig): void {
    const fullTitle = config.title.includes(this.siteName)
      ? config.title
      : `${config.title} | ${this.siteName}`;
    const description = (config.description || '').slice(0, 160).trim();
    const url = config.url ? (config.url.startsWith('http') ? config.url : `${this.siteBaseUrl}${config.url}`) : this.siteBaseUrl;
    const image = config.image || this.defaultImage;
    const type = config.type || 'website';
    const keywords = config.keywords || 'movies, TV shows, cinema, reviews, cast, trailer, ratings, The Scenetale';

    // 1. Page Title
    this.titleService.setTitle(fullTitle);

    // 2. Standard Meta Tags
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'keywords', content: keywords });
    this.metaService.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' });

    // 3. Open Graph Tags
    this.metaService.updateTag({ property: 'og:site_name', content: this.siteName });
    this.metaService.updateTag({ property: 'og:title', content: fullTitle });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:image', content: image });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:type', content: type });

    // 4. Twitter Card Tags
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: fullTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    this.metaService.updateTag({ name: 'twitter:image', content: image });

    // 5. Canonical Link Tag
    this.setCanonicalUrl(url);

    // 6. JSON-LD Structured Data
    if (config.jsonLd) {
      this.setJsonLd(config.jsonLd);
    }
  }

  /**
   * Specifically tuned SEO for Movie detail pages to rank top in Google
   * for movie name queries (e.g. "[Movie Name] (Year) - Cast, Trailer, Ratings").
   */
  public setMovieSeo(movie: any, slug: string): void {
    if (!movie) return;

    const title = movie.title || movie.original_title || movie.name || 'Movie';
    const year = movie.release_date ? new Date(movie.release_date).getFullYear() : '';
    const yearSuffix = year ? ` (${year})` : '';

    const seoTitle = `${title}${yearSuffix} - Reviews, Cast, Trailer & Ratings`;
    const cleanOverview = movie.overview ? movie.overview.replace(/\s+/g, ' ').trim() : '';
    const description = cleanOverview
      ? `Explore ${title}${yearSuffix} on The Scenetale. ${cleanOverview}`.slice(0, 155)
      : `Discover ${title}${yearSuffix} on The Scenetale: watch official trailers, explore cast & crew, read synopsis, user ratings, and trivia.`;

    const genreList = Array.isArray(movie.genres) ? movie.genres.map((g: any) => g.name).join(', ') : '';
    const keywords = `${title}, ${title} movie, ${title}${yearSuffix}, ${title} cast, ${title} trailer, ${title} review, ${title} rating, ${genreList}, The Scenetale`;

    const image = movie.backdrop_path
      ? `https://image.tmdb.org/t/p/w1280${movie.backdrop_path}`
      : (movie.poster_path ? `https://image.tmdb.org/t/p/w780${movie.poster_path}` : this.defaultImage);

    const movieUrl = `${this.siteBaseUrl}/movie/${slug}`;

    // Schema.org Movie Structured Data
    const movieJsonLd: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'Movie',
      'name': title,
      'image': image,
      'url': movieUrl,
      'description': cleanOverview || description,
      'inLanguage': movie.original_language || 'en',
      'publisher': {
        '@type': 'Organization',
        'name': this.siteName,
        'url': this.siteBaseUrl
      }
    };

    if (movie.release_date) {
      movieJsonLd['datePublished'] = movie.release_date;
    }

    if (Array.isArray(movie.genres) && movie.genres.length > 0) {
      movieJsonLd['genre'] = movie.genres.map((g: any) => g.name);
    }

    if (movie.vote_average && movie.vote_count) {
      movieJsonLd['aggregateRating'] = {
        '@type': 'AggregateRating',
        'ratingValue': movie.vote_average.toFixed(1),
        'bestRating': '10',
        'worstRating': '1',
        'ratingCount': movie.vote_count
      };
    }

    // BreadcrumbList for Google SERP breadcrumbs
    const breadcrumbJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Home',
          'item': this.siteBaseUrl
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Movies',
          'item': `${this.siteBaseUrl}/movies/popular`
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': title,
          'item': movieUrl
        }
      ]
    };

    this.setSeo({
      title: seoTitle,
      description,
      keywords,
      image,
      url: `/movie/${slug}`,
      type: 'video.movie',
      jsonLd: [movieJsonLd, breadcrumbJsonLd]
    });
  }

  /**
   * Specifically tuned SEO for TV Series detail pages.
   */
  public setTvSeo(tv: any, slug: string): void {
    if (!tv) return;

    const title = tv.name || tv.original_name || tv.title || 'TV Series';
    const year = tv.first_air_date ? new Date(tv.first_air_date).getFullYear() : '';
    const yearSuffix = year ? ` (${year})` : '';

    const seoTitle = `${title}${yearSuffix} - TV Series Episodes, Cast & Plot`;
    const cleanOverview = tv.overview ? tv.overview.replace(/\s+/g, ' ').trim() : '';
    const description = cleanOverview
      ? `Explore TV Series ${title}${yearSuffix} on The Scenetale. ${cleanOverview}`.slice(0, 155)
      : `Discover ${title}${yearSuffix} on The Scenetale: watch trailers, browse season episodes, cast list, and ratings.`;

    const genreList = Array.isArray(tv.genres) ? tv.genres.map((g: any) => g.name).join(', ') : '';
    const keywords = `${title}, ${title} TV show, ${title} series, ${title} episodes, ${title} cast, ${genreList}, The Scenetale`;

    const image = tv.backdrop_path
      ? `https://image.tmdb.org/t/p/w1280${tv.backdrop_path}`
      : (tv.poster_path ? `https://image.tmdb.org/t/p/w780${tv.poster_path}` : this.defaultImage);

    const tvUrl = `${this.siteBaseUrl}/tv/${slug}`;

    const tvJsonLd: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'TVSeries',
      'name': title,
      'image': image,
      'url': tvUrl,
      'description': cleanOverview || description,
      'inLanguage': tv.original_language || 'en',
      'publisher': {
        '@type': 'Organization',
        'name': this.siteName,
        'url': this.siteBaseUrl
      }
    };

    if (tv.first_air_date) {
      tvJsonLd['startDate'] = tv.first_air_date;
    }
    if (tv.number_of_seasons) {
      tvJsonLd['numberOfSeasons'] = tv.number_of_seasons;
    }
    if (tv.number_of_episodes) {
      tvJsonLd['numberOfEpisodes'] = tv.number_of_episodes;
    }
    if (tv.vote_average && tv.vote_count) {
      tvJsonLd['aggregateRating'] = {
        '@type': 'AggregateRating',
        'ratingValue': tv.vote_average.toFixed(1),
        'bestRating': '10',
        'worstRating': '1',
        'ratingCount': tv.vote_count
      };
    }

    const breadcrumbJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': this.siteBaseUrl },
        { '@type': 'ListItem', 'position': 2, 'name': 'TV Shows', 'item': `${this.siteBaseUrl}/tv/popular` },
        { '@type': 'ListItem', 'position': 3, 'name': title, 'item': tvUrl }
      ]
    };

    this.setSeo({
      title: seoTitle,
      description,
      keywords,
      image,
      url: `/tv/${slug}`,
      type: 'video.tv_show',
      jsonLd: [tvJsonLd, breadcrumbJsonLd]
    });
  }

  /**
   * Specifically tuned SEO for Actor / Director Person detail pages.
   */
  public setPersonSeo(person: any, slug: string): void {
    if (!person) return;

    const name = person.name || person.original_name || 'Celebrity';
    const dept = person.known_for_department || 'Acting';
    const seoTitle = `${name} - Filmography, Movies & Biography`;
    const cleanBio = person.biography ? person.biography.replace(/\s+/g, ' ').trim() : '';
    const description = cleanBio
      ? `${name} (${dept}): ${cleanBio}`.slice(0, 155)
      : `Explore ${name}'s complete filmography, top movies, TV series, awards, and biography on The Scenetale.`;

    const keywords = `${name}, ${name} movies, ${name} filmography, ${name} biography, ${name} awards, ${dept}, The Scenetale`;
    const image = person.profile_path ? `https://image.tmdb.org/t/p/w500${person.profile_path}` : this.defaultImage;
    const personUrl = `${this.siteBaseUrl}/person/${slug}`;

    const personJsonLd: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': name,
      'image': image,
      'url': personUrl,
      'jobTitle': dept,
      'description': cleanBio || description
    };

    if (person.birthday) {
      personJsonLd['birthDate'] = person.birthday;
    }
    if (person.place_of_birth) {
      personJsonLd['birthPlace'] = person.place_of_birth;
    }

    this.setSeo({
      title: seoTitle,
      description,
      keywords,
      image,
      url: `/person/${slug}`,
      type: 'profile',
      jsonLd: personJsonLd
    });
  }

  /**
   * Homepage SEO with Google SearchAction Schema.org markup.
   */
  public setHomeSeo(): void {
    const websiteJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': this.siteName,
      'url': this.siteBaseUrl,
      'potentialAction': {
        '@type': 'SearchAction',
        'target': `${this.siteBaseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    };

    const orgJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': this.siteName,
      'url': this.siteBaseUrl,
      'logo': `${this.siteBaseUrl}/assets/images/logo.png`
    };

    this.setSeo({
      title: 'The Scenetale | Cinema, Movies, TV Series & Stars',
      description: 'The Scenetale - Discover popular Hollywood movies, top-rated cinema, trending TV series, celebrity cast, and movie trivia.',
      keywords: 'Hollywood movies, popular movies, movies in theatres, TV shows, celebrity news, movie ratings, trailers, The Scenetale',
      image: `${this.siteBaseUrl}/assets/images/icon.png`,
      url: '/',
      type: 'website',
      jsonLd: [websiteJsonLd, orgJsonLd]
    });
  }

  /**
   * Injects or updates canonical URL link in document head.
   */
  private setCanonicalUrl(url: string): void {
    try {
      let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        this.document.head.appendChild(link);
      }
      link.setAttribute('href', url);
    } catch {
      // In SSR if document access is restricted
    }
  }

  /**
   * Injects or replaces JSON-LD structured data script in document head.
   */
  private setJsonLd(data: Record<string, any> | Array<Record<string, any>>): void {
    try {
      const scriptId = 'scenetale-seo-jsonld';
      let script: HTMLScriptElement | null = this.document.getElementById(scriptId) as HTMLScriptElement;

      if (!script) {
        script = this.document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        this.document.head.appendChild(script);
      }

      script.text = JSON.stringify(data);
    } catch {
      // In SSR if document access is restricted
    }
  }
}
